import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';

import type {
  AuthenticateTerraConnectionMutation,
  AuthenticateTerraConnectionMutationVariables,
  DeauthenticateTerraConnectionMutation,
  DeauthenticateTerraConnectionMutationVariables,
} from 'generated/terra.generated';
import {
  useAuthenticateTerraConnectionMutation,
  useDeauthenticateTerraConnectionMutation,
  useGetTerraConnectionsQuery,
} from 'generated/terra.generated';

import { GetTerraConnectionsDocument } from '../../generated/terra.generated';

export const useGetTerraConnections = (params: {
  vitall_patient_id: string;
}) => {
  const { handleError } = useResponseHandler();
  const { vitall_patient_id } = params;
  const response = useGetTerraConnectionsQuery({
    variables: { input: { vitall_patient_id } },
    skip: !vitall_patient_id,
  });
  handleError(response.error);

  return { ...response, data: response.data?.listTerraConnections };
};

export function useAuthenticateTerraConnection(): [
  (
    params: AuthenticateTerraConnectionMutationVariables
  ) => Promise<FetchResult<AuthenticateTerraConnectionMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useAuthenticateTerraConnectionMutation();
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'terra.authenticate',
  });

  const loading = baseLoading;

  async function authenticateConnection(
    params: AuthenticateTerraConnectionMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    handleError(response.errors);
    return response;
  }
  return [authenticateConnection, loading];
}

export function useDeauthenticateTerraConnection(): [
  (
    params: DeauthenticateTerraConnectionMutationVariables & {
      vitall_patient_id: string;
    }
  ) => Promise<FetchResult<DeauthenticateTerraConnectionMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeauthenticateTerraConnectionMutation();
  const loading = baseLoading;

  async function deauthenticateConnection(
    params: DeauthenticateTerraConnectionMutationVariables & {
      vitall_patient_id: string;
    }
  ) {
    const { terra_connection_id } = params.input;
    const response = await mutate({
      variables: {
        input: { terra_connection_id },
      },
      refetchQueries: [
        {
          query: GetTerraConnectionsDocument,
          variables: {
            input: {
              vitall_patient_id: params.vitall_patient_id,
            },
          },
        },
      ],
    });
    return response;
  }
  return [deauthenticateConnection, loading];
}
